import React,{Suspense, useEffect, useState, lazy} from 'react';
import { LazyLoadComponent } from 'react-lazy-load-image-component';
import PropTypes from 'prop-types';
import ImageFactory from '../../adapters/cloudinary/ImageFactory';
import Block from '../../adapters/helpers/Block';
import { IArticleOverview } from '../../types';
import { AdditionalReadingBlockConstants, ComponentFactoryConstants} from '../../adapters/helpers/Constants'
import { optimizeImage } from '../../adapters/helpers/pagespeedHelper';
import Link from 'next/link';
import Image from 'next/image';
const Heading = lazy(() => import('../Heading/Heading'));
const Loader = lazy(() => import('../../adapters/helpers/Loader'));

const IAdditionalReadingProps = {
    contentType: PropTypes.oneOf([AdditionalReadingBlockConstants.additionalReadingBlock]),
    className: PropTypes.string,
    classNames: PropTypes.string, // contentful misspelling
    name: PropTypes.string,
    title: PropTypes.string,
    anchorId: PropTypes.string,
    isLegacyLayout: PropTypes.bool,
    articles: PropTypes.arrayOf(IArticleOverview),
};

export default function AdditionalReadingBlock(props) {
    const { extraAttributes } = props;

    const block = new Block(props);
    const fields = block.getFields();

    return (
            <Suspense fallback={<Loader />}>
            <LazyLoadComponent offset={100}>
            <AdditionalReading
                {...fields}
                anchorId={block.getAnchorId()}
                articles={extraAttributes?.entity?.relatedArticles}
            />
      </LazyLoadComponent>
      </Suspense>
    );
}

AdditionalReadingBlock.propTypes = {
    document: PropTypes.shape({
        fields: PropTypes.exact(IAdditionalReadingProps),
    }),
    extraAttributes: PropTypes.shape({
        entity: PropTypes.shape({
            relatedArticles: PropTypes.arrayOf(IArticleOverview),
        }),
    }),
};

export function AdditionalReading(props) {
    const { classNames, className = classNames } = props;
    const { title, anchorId, articles, isLegacyLayout} = props;

    if (!articles || articles.length === 0) {
        return null;
    }

    const linkedArticles = articles.filter(article => (
        typeof article?.fields?.slug?.fields?.slug === 'string' 
    ));
    const variationB = (linkedArticles.length <= 2);
    return (
        <Suspense fallback={<Loader />}>
        <div className={`ob-additional-reading ${isLegacyLayout ? 'ob-additional-reading__legacy' : ''} ${variationB ? 'ob-additional-reading--wide' : ''} ${className || ''}`} id={anchorId}>
        <Suspense fallback={<Loader />}>
            <Heading className="ob-additional-reading__heading">
                {title}
            </Heading>
        </Suspense>

        <LazyLoadComponent offset={100}>
        <Suspense fallback={<Loader />}>
            <div className="ob-additional-reading__articles">
                <ul className="ob-additional-reading__list">
                    {linkedArticles.map((article, i) => {
                        const contentThumbnail = article.fields?.thumbnailImage?.fields?.asset?.fields?.file?.url;                                     
                        if (article.fields?.slug?.fields?.slug) {
                            return (
                                <li className="ob-additional-reading__item" key={i}>
                                    <Link className="ob-additional-reading__item-link" href={article.fields?.slug?.fields?.slug}>
                                        <div className="ob-additional-reading__item-image">
                                            {!!contentThumbnail && (
                                                <LazyLoadComponent offset={100} placeholder={
                                                    <picture className="ob-additional-reading__item-pic">
                                                        <Image
                                                            className="ob-additional-reading__item-img"
                                                            alt={`${article.fields?.title} ${AdditionalReadingBlockConstants.articleLink}`}
                                                            width={0}
                                                            height={0}
                                                            sizes="(max-width: 768px) 50vw (max-width: 1200px) 100vw 70vw"
                                                            style={{ width: 'auto', height: 'auto' }} // optional
                                                        />
                                                    </picture>
                                                }>
                                                    <picture className="ob-additional-reading__item-pic">
                                                        {variationB && (
                                                            <source media={ComponentFactoryConstants.mediaUpper} srcSet={optimizeImage(ImageFactory.imageContentfullDpr(contentThumbnail, ComponentFactoryConstants.dprUpper))} />
                                                        )}

                                                        <source media={ComponentFactoryConstants.mediaLower} srcSet={optimizeImage(ImageFactory.imageContentfullDpr(contentThumbnail, ComponentFactoryConstants.dprMedium))} />
                                                        <source srcSet={optimizeImage(ImageFactory.imageContentfullDpr(contentThumbnail, ComponentFactoryConstants.dprAuto))} />

                                                        <Image
                                                            className="ob-additional-reading__item-img"
                                                             alt={`${article.fields?.title} ${AdditionalReadingBlockConstants.articleLink}`} 
                                                            src={optimizeImage(ImageFactory.imageContentfullSrc(contentThumbnail, ComponentFactoryConstants.dprUpper))}
                                                            srcSet={optimizeImage(ImageFactory.imageContentfullDpr(contentThumbnail, ComponentFactoryConstants.dprUpper))}
                                                            width={0}
                                                            height={0}
                                                            sizes="(max-width: 768px) 50vw (max-width: 1200px) 100vw 70vw"
                                                            style={{ width: 'auto', height: 'auto' }} // optional
                                                        />
                                                    </picture>
                                                </LazyLoadComponent>
                                            )}
                                        </div>

                                        <div className="ob-additional-reading__item-info">
                                            <div className="ob-additional-reading__item-title">
                                                {article.fields?.title}
                                            </div>

                                            {!!article.fields?.readTime && (
                                                <div className="ob-additional-reading__item-time">
                                                    {article.fields?.readTime} min read
                                                </div>
                                            )}
                                        </div>
                                    </Link>
                                </li>
                            );
                        } else {
                            return '';
                        }
                    })}
                </ul>
            </div>
            </Suspense>
        </LazyLoadComponent>
        </div>
         </Suspense>
    );
}

AdditionalReading.propTypes = IAdditionalReadingProps;
